import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import { useMutation, useQuery } from '@tanstack/react-query'
import { captureException } from '@sentry/react'
import { v4 as uuid } from 'uuid'
import { theme } from '../../../theme/theme'
import { NotificationAvatar } from '../NotificationAvatar'
import { NotificationData } from '../../../types/notifications'
import { formatDate } from '../../../helpers/date'
import { INotificationModel } from '../../../interfaces/models/notifications.interfaces'
import {
  SocietyNavigationState,
  societyNavigationStateAtom,
} from '../../../views/society/atom'
import { setRead } from '../../../api/notifications'
import { useSelectedSocietyId } from '../../../hooks/useSelectedSocietyContext'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { useAuth } from '../../../hooks/useAuth'
import { postQueries } from '../../../api/posts'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { societyQueries } from '../../../api/societies'
import { userQueries } from '../../../api/users'
import { getUserFullName } from '../../../helpers/user'
import { removeMarkdown, replaceHtml } from '../../../helpers/string'

interface NotificationProps {
  notification: INotificationModel
}

const getBoldText = (_string: string): JSX.Element => {
  return (
    <span key={uuid()} style={{ fontWeight: 'bold' }}>
      {_string}
    </span>
  )
}

export const Notification = ({
  notification,
}: NotificationProps): React.ReactNode => {
  const notificationData = notification.data
    ? (JSON.parse(notification.data) as NotificationData)
    : undefined

  const navigate = useNavigate()
  const [, setSocietyNavigationState] = useAtom(societyNavigationStateAtom)

  const { mutate: mutateSetRead } = useMutation({
    mutationFn: setRead,
    meta: { skipGlobalInvalidate: true },
    onError: (error) => {
      captureException(error)
    },
  })
  const { setCurrentSocietyId } = useSelectedSocietyId()

  const onNotificationClick = (): void => {
    mutateSetRead({ notificationIds: [notification._id] })
    switch (notification.type) {
      case 'society-request-non-member':
      case 'society-request-shared-home':
      case 'society-request-member':
        setCurrentSocietyId(notification.societyId as string)
        setSocietyNavigationState(SocietyNavigationState.SocietyRequests)
        navigate(
          reverseUrl('management:register-member', {
            id: notification.societyId,
          })
        )
        break
      case 'hey-new-posts':
      case 'hey-new-comments':
      case 'hey-new-likes':
      case 'feed-new-likes':
      case 'feed-new-comments':
        navigate(
          `${reverseUrl('post:detail', {
            id: notificationData?.post._id,
          })}${
            notification.secondaryDocId
              ? `?commentId=${notification.secondaryDocId}`
              : ''
          }`
        )
        break
      case 'new-ticket':
        navigate(
          `${reverseUrl('management:ticket-detail', {
            id: notificationData?.ticket.societyId,
            ticketId: notificationData?.ticket.ticketId,
          })}`
        )
        break
      case 'new-ticket-from-board':
        navigate(
          `${reverseUrl('society:ticket-detail', {
            id: notificationData?.ticket.societyId,
            ticketId: notificationData?.ticket.ticketId,
          })}`
        )
        break
      case 'ticket-status':
        navigate(
          `${reverseUrl('society:ticket-detail', {
            id: notificationData?.ticket.societyId,
            ticketId: notificationData?.ticket.ticketId,
          })}`
        )
        break
      case 'ticket-assigned':
        navigate(
          `${reverseUrl('management:ticket-detail', {
            id: notificationData?.ticket.societyId,
            ticketId: notificationData?.ticket.ticketId,
          })}`
        )
        break
      case 'chat-interests-new-channel':
        navigate(reverseUrl('chat:detail', { id: notificationData?.room._id }))
        break
      case 'society-request-accepted':
        setCurrentSocietyId(notification.societyId as string)
        navigate(
          reverseUrl('society:home', {
            id: notification.societyId,
          })
        )
        break
      case 'contract-monitoring':
        setCurrentSocietyId(notification.societyId as string)
        navigate(
          reverseUrl('management:contracts-detail', {
            id: notification.societyId,
            contractId: notificationData?.contract._id,
          })
        )
        break
      case 'new-binder-document':
        setCurrentSocietyId(notification.societyId as string)
        if (notificationData?.document.parentId) {
          navigate(
            reverseUrl('society:binder-detail', {
              id: notification.societyId,
              unitId: notificationData?.document.unitId,
              documentId: notificationData?.document.parentId,
            })
          )
        } else {
          navigate(
            reverseUrl('society:binder-unit', {
              id: notification.societyId,
              unitId: notificationData?.document.unitId,
            })
          )
        }

        break
      case 'new-binder-information':
        setCurrentSocietyId(notification.societyId as string)
        if (notificationData?.document.parentId) {
          navigate(
            reverseUrl('society:binder-detail', {
              id: notification.societyId,
              unitId: notificationData?.document.unitId,
              documentId: notificationData?.document.parentId,
            })
          )
        } else {
          navigate(
            reverseUrl('society:binder-unit', {
              id: notification.societyId,
              unitId: notificationData?.document.unitId,
            })
          )
        }

        break
      default:
        captureException(
          new Error(
            `No onClick action defined for notification with type ${notification.type}`
          )
        )
    }
  }

  const { userId: authUserId } = useAuth()
  const userId = authUserId as string

  const postId =
    notification.type === 'feed-new-comments'
      ? notification.primaryDocId
      : undefined
  const { data: post } = useQuery(postQueries.getPost(postId))

  const getTranslationKey = (type: INotificationModel['type']): string => {
    switch (type) {
      case 'chat-interests-new-channel':
        return 'notifications.chatNewChannel'
      case 'hey-new-posts':
        return `notifications.newPost`
      case 'hey-new-comments':
      case 'feed-new-comments': {
        if (post && post.author === userId) {
          return 'notifications.newCommentYourPost'
        }
        return 'notifications.newComment'
      }
      case 'hey-new-likes':
      case 'feed-new-likes':
        return 'notifications.newLike'
      case 'society-request-accepted':
        return 'notifications.societyRequestAccepted'
      case 'society-request-shared-home':
      case 'society-request-member':
      case 'society-request-non-member':
        return 'notifications.societyRequest'
      case 'society-activated-shared':
      case 'society-activated-legal':
      case 'society-activated-nonlegal':
        return 'notifications.societyActivated'
      case 'invite-all-to-household':
      case 'invite-all-to-society':
      case 'invite-to-neighbourhood':
      case 'invite-to-shared-home':
        return 'notifications.societyInvite'
      case 'service-report':
        return 'notifications.newProblemReport'
      case 'admins-society-request-accepted':
        return 'notifications.societyRequestAcceptedAdmin'
      case 'app-new-inspirations':
        return 'notifications.newInspiration'
      case 'contract-monitoring':
        return 'notifications.contractMonitoring'
      case 'new-binder-document':
        return notificationData?.document.type === 'folder'
          ? 'notifications.newBinderFolder'
          : 'notifications.newBinderDocument'
      case 'new-binder-information':
        return 'notifications.newBinderInformation'
      // These are not implemented in old app
      case 'new-ticket':
        return 'notifications.newTicket'
      case 'new-ticket-from-board':
        return 'notifications.newTicketFromBoard'
      case 'ticket-status': {
        if (notificationData?.ticket.status === 'closed') {
          return 'notifications.ticketClosed'
        }
        return 'notifications.ticketOpen'
      }
      case 'ticket-assigned':
        return 'notifications.ticketAssigned'
      case 'user-added-to-unit':
      case 'invite-all-admin-to-society':
      case 'chat-oneonone-new-message':
      case 'chat-board-new-message':
      case 'chat-interests-new-message':
      default:
        return ''
    }
  }

  const { translate } = useAppTranslation()

  const { data: society } = useQuery(
    societyQueries.getSociety(notification.societyId)
  )

  const getSocietyText = (): React.ReactNode => {
    if (notificationData?.society) {
      const { name } = notificationData.society
      return getBoldText(name)
    }
    if (notification.societyId) {
      if (society) {
        const { name } = society
        if (name) {
          return getBoldText(name)
        }
      }
    }

    return <React.Fragment key={uuid()} />
  }

  const { data: author } = useQuery(userQueries.getUser(notification.authorId))

  const getUserText = (): JSX.Element => {
    if (notificationData?.from) {
      const { name, surname } = notificationData.from
      return getBoldText(`${name} ${surname}`)
    }

    if (notification.authorId) {
      if (author) {
        return getBoldText(getUserFullName(author))
      }
    }

    return <React.Fragment key={uuid()} />
  }

  const getMessageValue = (
    key: string,
    _notificationData: NotificationData
  ): React.ReactNode => {
    switch (key) {
      case '{% userName %}':
        return getUserText()
      case '{% societyName %}':
        return getSocietyText()
      case '{% roomName %}': {
        return getBoldText(_notificationData.room.name)
      }
      case '{% contractTitle %}': {
        return getBoldText(_notificationData.contract.title)
      }
      case '{% unitName %}': {
        return <span key={uuid()}>{_notificationData.document.unitString}</span>
      }
      case '{% ticketTitle %}': {
        return getBoldText(_notificationData.ticket.title)
      }
      case '{% message %}': {
        return (
          <span key={uuid()}>
            {removeMarkdown(replaceHtml(_notificationData.post.message))}
          </span>
        )
      }
      case '{% comment %}': {
        return (
          <span key={uuid()}>
            {removeMarkdown(replaceHtml(_notificationData.comment.message))}
          </span>
        )
      }
      default:
        return <span key={uuid()}>{key}</span>
    }
  }

  if (!notificationData) {
    return null
  }
  const test = getTranslationKey(notification.type)
  const notificationMessage = translate(test)
  const splitMessage = notificationMessage.split(/({% [A-Za-z]+ %})/)
  const message = splitMessage.map((string) =>
    getMessageValue(string, notificationData)
  )

  return (
    <>
      <div
        className={`flex min-h-[40px] w-80 cursor-pointer gap-2 text-sm hover:bg-neutral-96
            first:hover:rounded-t last:hover:rounded-b md:w-96
          `}
      >
        <button
          style={{
            backgroundColor: !notification.isRead
              ? theme.colors.unreadNotificationBackground
              : 'inherit',
          }}
          className="flex w-full gap-3 border-b border-neutral-96 px-4 pt-3 pb-4"
          onClick={onNotificationClick}
        >
          <NotificationAvatar
            notification={notification}
            notificationData={notificationData}
          />

          <div className="-mt-[2px] flex w-full flex-col items-start gap-1">
            <div className="text-left line-clamp-4 md:line-clamp-3">
              {message}
            </div>
            <p className="text-neutral-50" style={theme.textVariants.caption}>
              {formatDate(Date.parse(notification.createdAt), 'short')}
            </p>
          </div>
        </button>
      </div>
    </>
  )
}
